import React from 'react';
import { Link } from 'react-router-dom';
import { SCREENS } from '../../../services/exports/Constants';
import useNavigate from '../../../hooks/navigation/useNavigate';
import { Props } from './GiftCardTermsAndConditions';

const De = (props: Props) => {
  const { generatePath } = useNavigate();
  const {
    companyLegalName,
    companyAddress,
    companyLegalEmail,
    companyLegalWebsite,
    companyLegalBusinessName,
    companyLegalPrivacyUrl,
    agbVersionDate,
    companyLegalCity,
    companyCountryName,
    currencyName,
  } = props;

  return (
    <article className="mx-auto w-full max-w-4xl px-md pb-md">
      <header className="py-md bg-white">
        <h1 className="text-2xl font-semibold leading-tight">
          Allgemeine Geschäftsbedingungen für den Verkauf von {companyLegalName} Gutscheinen
        </h1>
        <p className="mt-2 text-sm text-slate-600">Stand: {agbVersionDate}</p>
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-medium text-slate-900">{companyLegalName}</p>
          <p className="text-sm text-slate-700">{companyAddress}</p>
          <div className="mt-3 flex flex-col gap-1 text-sm text-slate-700">
            <span>
              E-Mail:{' '}
              <a className="underline underline-offset-2 hover:text-slate-900" href={`mailto:${companyLegalEmail}`}>
                {companyLegalEmail}
              </a>
            </span>
            <span>
              Internet:{' '}
              <a
                className="underline underline-offset-2 hover:text-slate-900"
                href={companyLegalWebsite}
                target="_blank"
                rel="noreferrer"
              >
                {companyLegalWebsite}
              </a>
            </span>
          </div>
        </div>
      </header>

      <div className="prose prose-slate max-w-none space-y-sm">
        <h2>1. Geltungsbereich und Vertragspartner</h2>
        <p>
          <strong>1.1.</strong> Diese Allgemeinen Geschäftsbedingungen („AGB“) gelten für den Verkauf von
          Gastronomie-Gutscheinen („Gutscheine“) durch {companyLegalName}, {companyAddress}.
        </p>
        <p>
          <strong>1.2.</strong> Der Kaufvertrag über den Gutschein kommt zwischen dir und {companyLegalName} zustande.
          <br />
          <strong>Kontakt:</strong>
          <br />
          E-Mail: {companyLegalEmail}
          <br />
          Internet: {companyLegalWebsite}
        </p>
        <p>
          <strong>1.3.</strong> Abweichende Allgemeine Geschäftsbedingungen des Kunden werden nicht anerkannt, es sei
          denn, {companyLegalBusinessName} stimmt ihrer Geltung ausdrücklich schriftlich zu.
        </p>

        <h2>2. Vertragsschluss</h2>
        <p>
          <strong>2.1.</strong> Die Darstellung der Gutscheine im Online-Shop von {companyLegalBusinessName} stellt noch
          kein rechtlich bindendes Angebot dar, sondern eine unverbindliche Aufforderung, Gutscheine zu bestellen.
        </p>
        <p>
          <strong>2.2.</strong> Vor Abgabe deiner Bestellung werden alle Eingaben in einer Bestellübersicht angezeigt
          und können dort korrigiert werden. Erst durch Anklicken des Buttons „Jetzt kaufen“ gibst du ein verbindliches
          Angebot zum Abschluss eines Kaufvertrags über die im Warenkorb befindlichen Gutscheine ab.
        </p>
        <p>
          <strong>2.3.</strong> Nach Absenden der Bestellung erhältst du eine automatische Bestellbestätigung per
          E-Mail. Mit Zugang dieser Bestellbestätigung kommt der Kaufvertrag zustande.
        </p>
        <p>
          <strong>2.4.</strong> Der Vertragstext wird von {companyLegalBusinessName} nicht dauerhaft im Kundenkonto
          gespeichert. Du erhältst die Bestelldaten sowie diese AGB als PDF per E-Mail und kannst sie darüber hinaus auf
          der Website von {companyLegalBusinessName} einsehen, speichern und ausdrucken.
        </p>
        <p>
          <strong>2.5.</strong> Vertragssprache ist Deutsch.
        </p>

        <h2>3. Der {companyLegalName}-Gutschein</h2>
        <p>
          <strong>3.1.</strong> Die von {companyLegalBusinessName} ausgegebenen Gutscheine berechtigen den Inhaber, in
          den von {companyLegalBusinessName} betriebenen {companyLegalName}s zubereitete Speisen und Getränke im Wert
          des auf dem Gutschein ausgewiesenen Nennbetrags zu beziehen.
        </p>
        <p>
          <strong>3.2.</strong> Soweit nicht ausdrücklich anders angegeben, dürfen mit dem Gutschein ausschließlich
          gastronomische Leistungen (Speisen und Getränke) bezahlt werden. Der Erwerb anderer Waren oder
          Dienstleistungen ist mit dem Gutschein nur zulässig, wenn dies ausdrücklich von {companyLegalBusinessName}{' '}
          gestattet wird.
        </p>
        <p>
          <strong>3.3.</strong> Gutscheine können, sofern im Bestellprozess nicht anders angegeben, für alle von{' '}
          {companyLegalBusinessName} betriebenen Standorte eingelöst werden. Ob und welche Standorte teilnehmen, kann im
          Zweifel der Webseite {companyLegalWebsite} oder den Informationen auf dem Gutschein entnommen werden.
        </p>
        <p>
          <strong>3.4.</strong> Die Gutscheine sind drei Jahre gültig, beginnend mit dem Ende des Kalenderjahres, in dem
          der Gutschein gekauft wurde, sofern auf dem Gutschein oder im Bestellprozess kein anderer Zeitraum angegeben
          ist.
        </p>
        <p>
          <strong>3.5.</strong> Eine Auszahlung des Gutscheinnennbetrags in Geld ist – vorbehaltlich etwaiger zwingender
          gesetzlicher Ansprüche – ausgeschlossen.
        </p>
        <p>
          <strong>3.6.</strong> Der Gutschein ist übertragbar. {companyLegalBusinessName} ist berechtigt, jedoch nicht
          verpflichtet, die materielle Berechtigung des Einlösenden über die bloße Vorlage des Gutscheins hinaus zu
          prüfen.
        </p>

        <h2>4. Gutscheine als Geschenk</h2>
        <p>
          <strong>4.1.</strong> Gutscheine können an Dritte verschenkt und von diesen im Rahmen der Gutscheinbedingungen
          eingelöst werden.
        </p>
        <p>
          <strong>4.2.</strong> Der jeweils aktuelle Guthabenstand eines Gutscheins ergibt sich aus den Einlösungen des
          Gutscheins im {companyLegalName}. Ein Anspruch auf Auskunft über den Guthabenstand besteht nur im Rahmen der
          technischen Möglichkeiten von {companyLegalBusinessName}.
        </p>

        <h2>5. Einlösung des Gutscheins</h2>
        <p>
          <strong>5.1.</strong> Zur Einlösung ist der Gutschein (in Papierform oder als PDF/Code auf dem Smartphone) im
          {companyLegalName} vorzulegen. Das Servicepersonal erfasst den Gutscheincode im Kassensystem und rechnet den
          Gutscheinbetrag auf deine Rechnung an.
        </p>
        <p>
          <strong>5.2.</strong> Reicht der Wert des Gutscheins zur Begleichung der gesamten Rechnung nicht aus, kann der
          verbleibende Betrag mit den im {companyLegalName} angebotenen Zahlungsmitteln beglichen werden.
        </p>
        <p>
          <strong>5.3.</strong> Übersteigt der Gutscheinwert den Rechnungsbetrag, kann – sofern technisch möglich – das
          Restguthaben auf dem Gutschein gespeichert bleiben und bei einem späteren Besuch eingesetzt werden. Ist dies
          nicht möglich, wird der Differenzbetrag nicht in bar ausgezahlt, sofern keine zwingenden gesetzlichen
          Regelungen entgegenstehen.
        </p>
        <p>
          <strong>5.4.</strong> Bei Verlust, Diebstahl oder Missbrauch des Gutscheins übernimmt{' '}
          {companyLegalBusinessName} keine Haftung für einen unbefugten Einsatz des Gutscheins. Behandle deinen
          Gutschein daher wie Bargeld.
        </p>

        <h2>6. Liefermodalitäten</h2>
        <p>
          <strong>6.1.</strong> Je nach Auswahl im Bestellprozess stellt {companyLegalBusinessName} den Gutschein in
          elektronischer Form (per E-Mail als PDF mit Gutscheincode) oder in körperlicher Form (z.B. Geschenkkarte per
          Post) zur Verfügung.
        </p>
        <p>
          <strong>6.2.</strong> Elektronische Gutscheine werden in der Regel innerhalb eines Werktags nach
          Vertragsschluss an die von dir angegebene E-Mail-Adresse versendet.
        </p>
        <p>
          <strong>6.3.</strong> Soweit ein Versand physischer Gutscheine angeboten wird, erfolgt die Lieferung
          ausschließlich an Adressen in folgendem Land: {companyCountryName}. Die Lieferzeit beträgt, sofern nicht
          anders angegeben, bis zu fünf Werktage ab Vertragsschluss. Werktage sind alle Tage von Montag bis Freitag ohne
          gesetzliche Feiertage am Sitz von {companyLegalBusinessName}.
        </p>

        <h2>7. Preise und Zahlungsbedingungen</h2>
        <p>
          <strong>7.1.</strong> Alle Preise verstehen sich als Gesamtpreise in {currencyName}, inklusive der jeweils
          gültigen gesetzlichen Umsatzsteuer, sofern nicht ausdrücklich etwas anderes angegeben ist.
        </p>
        <p>
          <strong>7.2.</strong> Gegebenenfalls zusätzlich anfallende Versandkosten werden im Bestellprozess gesondert
          ausgewiesen.
        </p>
        <p>
          <strong>7.3.</strong> Die im Online-Shop akzeptierten Zahlungsmittel (z.B. Kreditkarte, PayPal,
          Sofortüberweisung) werden im Bestellprozess angezeigt. {companyLegalBusinessName} behält sich vor, einzelne
          Zahlungsmethoden im Einzelfall auszuschließen.
        </p>
        <p>
          <strong>7.4.</strong> Der Kaufpreis ist mit Abschluss des Vertrages sofort fällig.
        </p>

        <h2>8. Datenschutz</h2>
        <p>
          <strong>8.1.</strong> {companyLegalBusinessName} verarbeitet personenbezogene Daten im Rahmen der
          Gutscheinbestellung ausschließlich im Einklang mit den geltenden Datenschutzbestimmungen.
        </p>
        <p>
          <strong>8.2.</strong> Näheres zur Datenverarbeitung ergibt sich aus der jeweils aktuellen
          Datenschutzerklärung, die unter&nbsp;
          <Link to={generatePath(SCREENS.PRIVACY_POLICY)} target="_blank" className="underline">
            {companyLegalPrivacyUrl}
          </Link>
          &nbsp;abrufbar ist.
        </p>

        <h2>9. Widerrufsrecht für Verbraucher – physische Gutscheine</h2>
        <p>
          <strong>9.1.</strong> Sofern du Verbraucher bist und einen physischen Gutschein (z.B. per Post versandte
          Gutscheinkarte) erwirbst, steht dir ein gesetzliches Widerrufsrecht zu, über das {companyLegalBusinessName}{' '}
          wie folgt informiert:
        </p>

        <div className="not-prose rounded-xl border border-slate-200 bg-slate-50 p-md">
          <p className="font-semibold">Widerrufsbelehrung für physische Gutscheine</p>
          <p className="mt-2">
            Du hast das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.
          </p>
          <p className="mt-2">
            Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag, an dem du oder ein von dir benannter Dritter, der nicht
            der Beförderer ist, den Gutschein in Besitz genommen hat.
          </p>
          <p className="mt-2">
            Um dein Widerrufsrecht auszuüben, musst du uns
            {companyLegalName}
            <br />
            {companyAddress}
            <br />
            E-Mail: {companyLegalEmail}
            <br />
            mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief oder E-Mail) über deinen
            Entschluss, diesen Vertrag zu widerrufen, informieren. Du kannst dafür das unten angefügte
            Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist.
          </p>
          <p className="mt-2">
            Zur Wahrung der Widerrufsfrist reicht es aus, dass du die Mitteilung über die Ausübung des Widerrufsrechts
            vor Ablauf der Widerrufsfrist absendest.
          </p>
          <p className="mt-2 font-semibold">Folgen des Widerrufs</p>
          <p className="mt-2">
            Wenn du diesen Vertrag widerrufst, erstatten wir dir alle Zahlungen, die wir von dir erhalten haben,
            einschließlich der Lieferkosten (mit Ausnahme etwaiger zusätzlicher Kosten, die sich daraus ergeben, dass du
            eine andere Art der Lieferung als die von uns angebotene, günstigste Standardlieferung gewählt hast),
            unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurück, an dem die Mitteilung über deinen
            Widerruf dieses Vertrags bei uns eingegangen ist.
          </p>
          <p className="mt-2">
            Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das du bei der ursprünglichen Transaktion
            eingesetzt hast, es sei denn, mit dir wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden dir
            wegen dieser Rückzahlung Entgelte berechnet.
          </p>
          <p className="mt-2">
            Wir können die Rückzahlung verweigern, bis wir den Gutschein wieder zurückerhalten haben oder bis du den
            Nachweis erbracht hast, dass du den Gutschein zurückgesandt hast, je nachdem, welches der frühere Zeitpunkt
            ist.
          </p>
          <p className="mt-2">
            Du hast den Gutschein unverzüglich und in jedem Fall spätestens binnen vierzehn Tagen ab dem Tag, an dem du
            uns über den Widerruf dieses Vertrags unterrichtest, an uns zurückzusenden oder zu übergeben. Die Frist ist
            gewahrt, wenn du den Gutschein vor Ablauf der Frist von vierzehn Tagen absendest. Wir tragen die Kosten der
            Rücksendung des Gutscheins.
          </p>
          <p className="mt-2">
            Du musst für einen etwaigen Wertverlust des Gutscheins nur aufkommen, wenn dieser Wertverlust auf einen zur
            Prüfung der Beschaffenheit, Eigenschaften und Funktionsweise des Gutscheins nicht notwendigen Umgang mit ihm
            zurückzuführen ist.
          </p>
          <p className="mt-2">Ende der Widerrufsbelehrung</p>
        </div>

        <h2>10. Widerrufsrecht für Verbraucher – elektronische Gutscheine</h2>
        <p>
          <strong>10.1.</strong> Sofern du Verbraucher bist und einen elektronischen Gutschein (z.B. per E-Mail
          versendetes PDF) erwirbst, steht dir grundsätzlich ein gesetzliches Widerrufsrecht zu.
        </p>
        <p>
          <strong>10.2.</strong> Das Widerrufsrecht erlischt jedoch, wenn {companyLegalBusinessName} mit deiner
          ausdrücklichen Zustimmung bereits vor Ablauf der Widerrufsfrist mit der Ausführung des Vertrages begonnen und
          den Gutschein bereitgestellt hat und du gleichzeitig bestätigt hast, dass du mit Beginn der Ausführung dein
          Widerrufsrecht verlierst. Widerrufe im Übrigen werden nach den gesetzlichen Vorgaben behandelt; im Zweifel
          gelten die Regelungen aus Ziffer 9 entsprechend.
        </p>

        <h2>11. Muster-Widerrufsformular</h2>
        <div className="not-prose rounded-xl border border-slate-200 bg-white p-md">
          <p className="font-semibold">
            (Wenn du den Vertrag widerrufen willst, dann fülle bitte dieses Formular aus und sende es zurück.)
          </p>
          <p className="mt-3">
            An
            <br />
            {companyLegalName}
            <br />
            {companyAddress}
            <br />
            E-Mail: {companyLegalEmail}
          </p>

          <div className="mt-4 space-y-4 text-sm text-slate-800">
            <div>
              <p>Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über den Kauf des folgenden Gutscheins:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Bestellt am:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Name des Verbrauchers:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Anschrift des Verbrauchers:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Unterschrift des Verbrauchers (nur bei Mitteilung auf Papier)</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Datum:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
          </div>
        </div>

        <h2>12. Gewährleistung, Haftung, Reklamationen</h2>
        <p>
          <strong>12.1.</strong> Es gelten die gesetzlichen Gewährleistungsrechte.
        </p>
        <p>
          <strong>12.2.</strong> Ansprüche auf Schadensersatz sind ausgeschlossen, es sei denn, es liegt eine Verletzung
          wesentlicher Vertragspflichten (Kardinalpflichten), eine Verletzung von Leben, Körper oder Gesundheit oder
          eine Haftung nach dem Produkthaftungsgesetz oder eine sonstige zwingende Haftung vor.
        </p>
        <p>
          <strong>12.3.</strong> Bei Reklamationen oder Fragen zur Bestellung kannst du dich per E-Mail an{' '}
          {companyLegalEmail} wenden.
        </p>

        <h2>13. Anwendbares Recht und Gerichtsstand</h2>
        <p>
          <strong>13.1.</strong> Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Zwingende
          Verbraucherschutzvorschriften des Staates, in dem du deinen gewöhnlichen Aufenthalt hast, bleiben unberührt.
        </p>
        <p>
          <strong>13.2.</strong> Bist du Kaufmann, juristische Person des öffentlichen Rechts oder
          öffentlich-rechtliches Sondervermögen, ist ausschließlicher Gerichtsstand für alle Streitigkeiten aus
          Vertragsverhältnissen zwischen dir und {companyLegalBusinessName} der Sitz von {companyLegalCity}.
        </p>
      </div>
    </article>
  );
};

export default De;
